<template>
  <!--
    Create a coupon on the new Stripe account, with every option Stripe's create
    takes. Existing-customers-only is on by default: such a coupon is refused at
    signup and can only be attached from the kitchen.
  -->
  <v-dialog :value="value" max-width="680px" persistent scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi-ticket-percent-outline</v-icon>
        Create Coupon
        <v-spacer/>
        <v-btn icon :disabled="saving" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-text-field
          v-model="form.name"
          label="Name (required)"
          hint="Customers see this on their invoices, so don't use the code here"
          persistent-hint
          counter="40"
          outlined
          dense
          autofocus
          class="mb-2"
        />
        <v-text-field
          v-model="form.id"
          label="Code"
          hint="Leave blank for a random code nobody can guess, best when customers should not know it"
          persistent-hint
          outlined
          dense
          class="mb-2"
        />

        <v-radio-group v-model="form.type" row dense hide-details class="mt-0 mb-3">
          <v-radio label="Percentage off" value="percent"/>
          <v-radio label="Amount off" value="amount"/>
        </v-radio-group>
        <v-row dense>
          <v-col v-if="form.type === 'percent'" cols="6">
            <v-text-field v-model.number="form.percent" type="number" label="Percent off" suffix="%" outlined dense/>
          </v-col>
          <template v-else>
            <v-col cols="6">
              <v-text-field v-model.number="form.amount" type="number" label="Amount off" prefix="$" outlined dense/>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="form.currency" label="Currency" outlined dense/>
            </v-col>
          </template>
        </v-row>

        <v-row dense>
          <v-col cols="6">
            <v-select v-model="form.duration" :items="durations" label="Duration" outlined dense/>
          </v-col>
          <v-col v-if="form.duration === 'repeating'" cols="6">
            <v-text-field v-model.number="form.months" type="number" label="Months" outlined dense/>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="6">
            <v-text-field
              v-model.number="form.maxRedemptions"
              type="number"
              label="Redemption limit"
              hint="Blank for no limit"
              persistent-hint
              outlined
              dense
            />
          </v-col>
          <v-col cols="6">
            <v-menu v-model="showDatePicker" :close-on-content-click="false" offset-y>
              <template v-slot:activator="{on}">
                <v-text-field
                  :value="form.redeemBy"
                  label="Last redemption date"
                  hint="Blank for no end date"
                  persistent-hint
                  readonly
                  clearable
                  outlined
                  dense
                  v-on="on"
                  @click:clear="form.redeemBy = null"
                />
              </template>
              <v-date-picker v-model="form.redeemBy" no-title @change="showDatePicker = false"/>
            </v-menu>
          </v-col>
        </v-row>

        <v-checkbox
          v-model="form.existingCustomerOnly"
          label="Existing customers only"
          hint="Customers cannot enter this code at signup; it can only be added from the kitchen"
          persistent-hint
          class="mt-2"
        />

        <v-expansion-panels flat class="mt-3">
          <v-expansion-panel>
            <v-expansion-panel-header class="px-0">More Stripe options</v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-combobox
                v-model="form.products"
                label="Only for these Stripe products"
                hint="Product ids (prod_...). Fed's invoice lines carry no Stripe product, so a limited coupon discounts nothing on them."
                persistent-hint
                multiple
                small-chips
                deletable-chips
                outlined
                dense
                class="mb-3"
              />

              <template v-if="form.type === 'amount'">
                <div class="subtitle-2 mb-1">Amount in other currencies</div>
                <v-row v-for="(row, i) in form.currencyOptions" :key="`c${i}`" dense>
                  <v-col cols="5">
                    <v-text-field v-model="row.currency" label="Currency" outlined dense hide-details/>
                  </v-col>
                  <v-col cols="5">
                    <v-text-field v-model.number="row.amount" type="number" label="Amount off" prefix="$" outlined dense hide-details/>
                  </v-col>
                  <v-col cols="2">
                    <v-btn icon @click="form.currencyOptions.splice(i, 1)">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
                <v-btn text small class="mb-3" @click="form.currencyOptions.push({currency: '', amount: null})">
                  <v-icon left small>mdi-plus</v-icon>
                  Add currency
                </v-btn>
              </template>

              <div class="subtitle-2 mb-1">Metadata</div>
              <v-row v-for="(row, i) in form.metadata" :key="`m${i}`" dense>
                <v-col cols="5">
                  <v-text-field v-model="row.key" label="Key" outlined dense hide-details/>
                </v-col>
                <v-col cols="5">
                  <v-text-field v-model="row.value" label="Value" outlined dense hide-details/>
                </v-col>
                <v-col cols="2">
                  <v-btn icon @click="form.metadata.splice(i, 1)">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-btn text small @click="form.metadata.push({key: '', value: ''})">
                <v-icon left small>mdi-plus</v-icon>
                Add metadata
              </v-btn>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-alert v-if="form.duration !== 'once'" dense outlined type="warning" class="mt-3 mb-0">
          This coupon keeps discounting every invoice of a customer who holds it{{ form.duration === 'repeating' ? ' for the months above' : ', until it is removed' }}.
        </v-alert>
        <v-alert v-if="isGiftCardName" dense outlined type="warning" class="mt-3 mb-0">
          A name containing "gift card" makes an amount-off coupon a gift card: applying it adds credit instead of a discount.
        </v-alert>
        <v-alert v-if="error" dense outlined type="error" class="mt-3 mb-0">{{ error }}</v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn text :disabled="saving" @click="close">Cancel</v-btn>
        <v-btn color="primary" :disabled="!form.name.trim()" :loading="saving" @click="create">Create Coupon</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api';
import moment from 'moment';

const emptyForm = () => ({
  name: '',
  id: '',
  type: 'percent',
  percent: null,
  amount: null,
  currency: 'cad',
  duration: 'once',
  months: null,
  maxRedemptions: null,
  redeemBy: null,
  existingCustomerOnly: true,
  products: [],
  currencyOptions: [],
  metadata: [],
});

const isBlank = value => value === null || value === undefined || value === '';
const toCents = dollars => Math.round(dollars * 100);

export default {
  name: 'CouponCreateDialog',
  props: {
    value: {type: Boolean, default: false},
  },
  data() {
    return {
      form: emptyForm(),
      durations: [
        {text: 'Once', value: 'once'},
        {text: 'Repeating', value: 'repeating'},
        {text: 'Forever', value: 'forever'},
      ],
      showDatePicker: false,
      saving: false,
      error: null,
    };
  },
  computed: {
    isGiftCardName() {
      return this.form.type === 'amount' && /gift\s*card/i.test(this.form.name);
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.form = emptyForm();
        this.error = null;
      }
    },
  },
  methods: {
    close() {
      this.$emit('input', false);
    },

    requestBody() {
      const {form} = this;
      const isAmount = form.type === 'amount';
      return {
        id: form.id,
        name: form.name,
        percent_off: !isAmount && !isBlank(form.percent) ? form.percent : null,
        amount_off: isAmount && !isBlank(form.amount) ? toCents(form.amount) : null,
        currency: form.currency,
        duration: form.duration,
        duration_in_months: form.duration === 'repeating' && !isBlank(form.months) ? form.months : null,
        max_redemptions: isBlank(form.maxRedemptions) ? null : form.maxRedemptions,
        redeem_by: form.redeemBy ? moment(form.redeemBy).endOf('day').unix() : null,
        existing_customer_only: form.existingCustomerOnly,
        products: form.products,
        currency_options: isAmount
          ? Object.fromEntries(form.currencyOptions.map(row => [row.currency.trim().toLowerCase(), toCents(row.amount)]))
          : {},
        metadata: Object.fromEntries(form.metadata.map(row => [row.key.trim(), row.value])),
      };
    },

    async create() {
      this.saving = true;
      this.error = null;
      try {
        const {data} = await api.post('v2/stripe/coupons', this.requestBody());
        this.$store.commit('showSnackbar', {message: `Coupon ${data.coupon.id} created`, color: 'success', timeout: 6000});
        this.$emit('created', data.coupon);
        this.close();
      } catch (e) {
        this.error = (e.response && e.response.data && (e.response.data.message || e.response.data.error)) || e.message || 'Failed to create the coupon';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
